import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Elf } from "@/components/Elf";
import { Snowfall, TwinkleStars } from "@/components/Snowfall";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = isLogin ? await login(email, password) : await register(name, email, password, accepted);
      toast.success(isLogin ? `Bentornato, ${user.name}!` : `Benvenuto, ${user.name}!`);
      if (!isLogin) {
        toast.info("Ti abbiamo inviato un'email di conferma: aprila per sbloccare le caselle.");
      }
      const from = location.state?.from;
      navigate(from || (user.role === "admin" ? "/admin" : "/dashboard"), { replace: true });
    } catch (err) {
      setError(formatApiError(err, isLogin ? "Accesso non riuscito." : "Registrazione non riuscita."));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-amber-500/25 bg-[#0A1D15]/80 px-4 py-3 pl-11 text-sm text-[#FAF6EE] placeholder:text-emerald-100/40 outline-none transition-colors focus:border-amber-400/70";

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#06150F] px-4 py-12 text-[#FAF6EE]">
      {/* animated night sky */}
      <Snowfall />
      <TwinkleStars />
      <div
        aria-hidden
        className="pointer-events-none absolute right-6 top-8 h-16 w-16 rounded-full bg-[radial-gradient(circle_at_35%_35%,#FFFDF4,#F3E5AB_60%,#D9C88F)] shadow-[0_0_70px_rgba(243,229,171,0.5)] sm:right-16 sm:top-12 sm:h-24 sm:w-24"
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-red-800/25 blur-3xl"
        animate={{ x: [0, 60, 0], y: [0, -40, 0] }}
        transition={{ repeat: Infinity, duration: 18, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -right-32 bottom-1/4 h-96 w-96 rounded-full bg-emerald-700/20 blur-3xl"
        animate={{ x: [0, -50, 0], y: [0, 45, 0] }}
        transition={{ repeat: Infinity, duration: 22, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl"
        animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
        transition={{ repeat: Infinity, duration: 26, ease: "easeInOut" }}
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="relative rounded-3xl border border-amber-500/25 bg-[#0A1D15]/80 p-8 shadow-[0_30px_80px_rgba(0,0,0,0.55)] backdrop-blur-xl sm:p-10">
          <Elf variant="peek" className="absolute -right-4 -top-14 w-16 sm:-right-8 sm:w-20" />
          <Link to="/" className="mb-8 flex items-center justify-center gap-3" data-testid="auth-logo">
            <Logo className="h-11 w-11" />
            <span className="font-display text-2xl font-bold text-amber-100">Avvento d'Oro</span>
          </Link>

          <h1 className="font-hero text-center text-3xl font-semibold text-amber-100">
            {isLogin ? "Bentornato tra le sorprese" : "Crea il tuo account"}
          </h1>
          <p className="mt-2 text-center text-sm text-emerald-100/65">
            {isLogin
              ? "Accedi per aprire le tue caselle."
              : "Registrati per comprare o attivare il tuo calendario."}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-400/60" />
                <input
                  data-testid="auth-name-input"
                  type="text"
                  required
                  minLength={2}
                  placeholder="Il tuo nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputCls}
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-400/60" />
              <input
                data-testid="auth-email-input"
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber-400/60" />
              <input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={8}
                placeholder="Password (min. 8 caratteri)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </div>

            {isLogin && (
              <div className="text-right">
                <Link
                  to="/recupera-password"
                  data-testid="forgot-password-link"
                  className="text-xs font-semibold text-amber-300/80 transition-colors hover:text-amber-200"
                >
                  Password dimenticata?
                </Link>
              </div>
            )}

            {!isLogin && (
              <label className="flex items-start gap-2.5 text-xs leading-snug text-emerald-100/70" data-testid="auth-terms-label">
                <input
                  type="checkbox"
                  required
                  data-testid="auth-terms-checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-amber-500"
                />
                <span>
                  Accetto i{" "}
                  <Link to="/termini-e-condizioni" className="font-semibold text-amber-300 underline">
                    Termini e Condizioni
                  </Link>{" "}
                  e l'{" "}
                  <Link to="/privacy-policy" className="font-semibold text-amber-300 underline">
                    Informativa Privacy
                  </Link>
                  .
                </span>
              </label>
            )}

            {error && (
              <p data-testid="auth-error" className="rounded-lg border border-red-400/40 bg-red-900/20 px-4 py-2.5 text-sm text-red-200">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="auth-submit-button"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-600 px-6 py-3.5 font-bold text-[#1A1204] shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/30 disabled:opacity-60"
            >
              {busy ? "Un attimo..." : isLogin ? "Accedi" : "Registrati"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-emerald-100/60">
            {isLogin ? "Non hai un account?" : "Hai già un account?"}{" "}
            <Link
              to={isLogin ? "/registrati" : "/accedi"}
              data-testid="auth-switch-link"
              className="font-semibold text-amber-300 hover:text-amber-200"
            >
              {isLogin ? "Registrati" : "Accedi"}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
